import { useEffect, useRef, useState } from 'react'
import { useAppDispatch, useAppSelector, legsActions, runnersActions, raceActions } from '../store/index'
import { AppShell } from '../components/layout/AppShell'
import { legService } from '../services/legService'
import { runnerService } from '../services/runnerService'
import { raceService } from '../services/raceService'
import { formatDuration, formatTimePT } from '../utils/formatTime'
import { formatPace, computeLegPaceMultiplier } from '../utils/paceCalc'

export function TeamPage() {
  const dispatch = useAppDispatch()
  const scrollRef = useRef<HTMLDivElement>(null)
  useEffect(() => { if (scrollRef.current) scrollRef.current.scrollTop = 0 }, [])
  const logs = useAppSelector(state => state.legs.logs)
  const runners = useAppSelector(state => state.runners.items)
  const assignments = useAppSelector(state => state.legs.assignments)
  const [now, setNow] = useState(() => Date.now())

  useEffect(() => {
    const load = async () => {
      try {
        const [fetchedLogs, fetchedRunners, fetchedLegs, raceState] = await Promise.all([
          legService.getLegLogs(),
          runnerService.getRunners(),
          legService.getLegs(),
          raceService.getRaceState(),
        ])
        dispatch(legsActions.setLegLogs(fetchedLogs))
        dispatch(runnersActions.setRunners(fetchedRunners))
        dispatch(legsActions.setLegAssignments(fetchedLegs))
        dispatch(raceActions.setRaceStatus(raceState.raceStatus))
        if (raceState.raceStartTime) dispatch(raceActions.setRaceStartTime(raceState.raceStartTime))
      } catch (error) {
        console.error('Failed to load team data:', error)
      }
    }
    load()
  }, [dispatch])

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(id)
  }, [])

  const computeVanStats = (vanNumber: 1 | 2) => {
    const vanLogs = logs
      .filter(l => l.vanNumber === vanNumber && l.startTime)
      .sort((a, b) => (a.legNumber || 0) - (b.legNumber || 0))
    if (vanLogs.length === 0) return { elapsedSeconds: 0, pace: null, distance: 0, elevGain: 0, elevLoss: 0 }

    const completedLogs = vanLogs.filter(l => l.endTime)
    const activeLeg = vanLogs.find(l => l.startTime && !l.endTime)

    // Sum actual durations for completed legs
    const completedSeconds = completedLogs.reduce((sum, l) => sum + (l.actualDurationSeconds || 0), 0)

    // Add elapsed time for the currently running leg (if this van is active)
    const activeSeconds = activeLeg
      ? Math.max(0, Math.floor((now - new Date(activeLeg.startTime!).getTime()) / 1000))
      : 0

    const elapsedSeconds = completedSeconds + activeSeconds

    const completedDistance = completedLogs.reduce((sum, l) => sum + (l.distanceMiles || 0), 0)
    const activeDistance = activeLeg ? (activeLeg.distanceMiles || 0) : 0
    const distance = completedDistance + (activeLeg ? activeDistance : 0)

    let elevGain = 0, elevLoss = 0
    for (const log of [...completedLogs, ...(activeLeg ? [activeLeg] : [])]) {
      const a = assignments.find(a => a.id === log.legAssignmentId)
      if (a) { elevGain += a.elevationGain || 0; elevLoss += a.elevationLoss || 0 }
    }
    const pace = elapsedSeconds > 0 && distance > 0 ? (elapsedSeconds / 60) / distance : null
    return { elapsedSeconds, pace, distance, elevGain, elevLoss }
  }

  const van1 = computeVanStats(1)
  const van2 = computeVanStats(2)
  const total = (() => {
    const elapsedSeconds = van1.elapsedSeconds + van2.elapsedSeconds
    const distance = van1.distance + van2.distance
    const elevGain = van1.elevGain + van2.elevGain
    const elevLoss = van1.elevLoss + van2.elevLoss
    const pace = elapsedSeconds > 0 && distance > 0 ? (elapsedSeconds / 60) / distance : null
    return { elapsedSeconds, pace, distance, elevGain, elevLoss }
  })()

  const runnerStats = [...runners]
    .sort((a, b) => a.runnerNumber - b.runnerNumber)
    .map(runner => {
      const runnerAssignments = assignments.filter(a => a.runnerId === runner.id)
      const completedLogs = logs.filter(l =>
        runnerAssignments.some(a => a.id === l.legAssignmentId) && l.endTime
      )
      const totalDistance = completedLogs.reduce((sum, l) => sum + (l.distanceMiles || 0), 0)
      const totalDuration = completedLogs.reduce((sum, l) => sum + (l.actualDurationSeconds || 0), 0)
      const avgPace = totalDuration > 0 && totalDistance > 0 ? (totalDuration / 60) / totalDistance : null
      const legNumbers = runnerAssignments.map(a => a.legNumber).sort((x, y) => x - y)
      let elevGain = 0, elevLoss = 0
      for (const log of completedLogs) {
        const a = assignments.find(a => a.id === log.legAssignmentId)
        if (a) { elevGain += a.elevationGain || 0; elevLoss += a.elevationLoss || 0 }
      }
      return { runner, legNumbers, completedLegs: completedLogs.length, totalDistance, totalDuration, avgPace, elevGain, elevLoss }
    })

  const raceStartTime = useAppSelector(state => state.race.raceStartTime)
  const raceStatus = useAppSelector(state => state.race.status)

  const estimatedFinishTime = (() => {
    if (!raceStartTime || raceStatus === 'not_started') return null
    if (raceStatus === 'completed') {
      const leg36Log = logs.find(l => l.legNumber === 36 && l.endTime)
      return leg36Log?.endTime ? formatTimePT(new Date(leg36Log.endTime)) : null
    }
    let totalSeconds = 0
    for (const assignment of assignments) {
      const log = logs.find(l => l.legAssignmentId === assignment.id)
      if (log?.actualDurationSeconds) {
        totalSeconds += log.actualDurationSeconds
      } else {
        const runner = runners.find(r => r.id === assignment.runnerId)
        const basePace = runner?.expectedPace ?? 10
        const multiplier = computeLegPaceMultiplier(
          assignment.distanceMiles || 0,
          assignment.elevationGain || 0,
          assignment.elevationLoss || 0,
          assignment.difficultyRating
        )
        totalSeconds += Math.floor((assignment.distanceMiles || 0) * basePace * multiplier * 60)
      }
    }
    const finishMs = new Date(raceStartTime).getTime() + totalSeconds * 1000
    return formatTimePT(new Date(finishMs))
  })()

  const vanGroups: Array<{ van: 1 | 2; stats: typeof van1; runners: typeof runnerStats }> = [
    { van: 1, stats: van1, runners: runnerStats.filter(r => r.runner.vanNumber === 1) },
    { van: 2, stats: van2, runners: runnerStats.filter(r => r.runner.vanNumber === 2) },
  ]

  const StatCard = ({ label, stats, accent }: { label: string; stats: typeof van1; accent: string }) => (
    <div className={`bg-white rounded-lg border-2 ${accent} px-2 py-1 flex items-center gap-2 flex-wrap`}>
      <span className="text-xs font-bold text-gray-600">{label}</span>
      <span className="text-sm font-mono font-bold text-blue-600">{formatDuration(stats.elapsedSeconds)}</span>
      <span className="text-xs font-mono text-gray-500">{stats.pace != null ? `${formatPace(stats.pace)}/mi` : '—'}</span>
      <span className="text-xs text-gray-600"><span className="font-semibold">{stats.distance.toFixed(1)}</span> mi</span>
      <span className="text-xs font-mono">
        <span className="text-green-700">↑{stats.elevGain > 0 ? stats.elevGain.toLocaleString() : '0'}</span>
        {' '}<span className="text-orange-700">↓{stats.elevLoss > 0 ? stats.elevLoss.toLocaleString() : '0'}</span>
        <span className="text-gray-400"> ft</span>
      </span>
    </div>
  )

  return (
    <AppShell>
      <div className="flex flex-col overflow-hidden" style={{ height: 'calc(100dvh - 5rem)' }}>
        {/* Header */}
        <div className="px-3 pt-2 pb-1 flex-shrink-0">
          <h1 className="text-lg font-bold flex items-center gap-1.5">
            <svg className="w-5 h-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            Team
          </h1>
        </div>

        {/* Scrollable content */}
        <div ref={scrollRef} className="flex-1 overflow-y-auto px-3 pb-3 space-y-2 min-h-0">
          {/* Total summary card */}
          <StatCard label="Total" stats={total} accent="border-gray-300" />

          {/* Runner tables by van */}
          {vanGroups.map(({ van, stats, runners: vanRunners }) => (
            <div key={van}>
              {/* Van header with inline stats */}
              <div className={`flex items-center justify-between px-2 py-1 rounded-t-lg border-t border-x ${van === 1 ? 'bg-blue-50 border-blue-200' : 'bg-purple-50 border-purple-200'}`}>
                <h2 className="text-xs font-bold text-gray-700 flex items-center gap-1">
                  <span className={`inline-block w-2 h-2 rounded-full ${van === 1 ? 'bg-blue-500' : 'bg-purple-500'}`} />
                  Van {van}
                </h2>
                <div className="text-right">
                  <p className="text-xs text-gray-500 font-mono leading-none">
                    {formatDuration(stats.elapsedSeconds)}
                    {stats.pace != null && <span className="ml-1.5">{formatPace(stats.pace)}/mi</span>}
                    <span className="ml-1.5">{stats.distance.toFixed(1)} mi</span>
                  </p>
                  <p className="text-xs font-mono leading-none mt-0.5">
                    <span className="text-green-700">↑{stats.elevGain.toLocaleString()}</span>
                    {' '}<span className="text-orange-700">↓{stats.elevLoss.toLocaleString()}</span>
                    <span className="text-gray-400"> ft</span>
                  </p>
                </div>
              </div>
              <div className="bg-white rounded-b-lg border border-gray-200 overflow-hidden">
                <table className="w-full text-xs">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-1.5 py-1 text-left font-semibold text-gray-500 w-5">#</th>
                      <th className="px-1.5 py-1 text-left font-semibold text-gray-500">Runner</th>
                      <th className="px-1.5 py-1 text-center font-semibold text-gray-500 w-8">Legs</th>
                      <th className="px-1.5 py-1 text-center font-semibold text-gray-500 w-10">Mi</th>
                      <th className="px-1.5 py-1 text-center font-semibold text-gray-500 w-14">Time</th>
                      <th className="px-1.5 py-1 text-center font-semibold text-gray-500 w-14">Pace</th>
                      <th className="px-1.5 py-1 text-center font-semibold text-green-700 w-10">↑ft</th>
                      <th className="px-1.5 py-1 text-center font-semibold text-orange-700 w-10">↓ft</th>
                    </tr>
                  </thead>
                  <tbody>
                    {vanRunners.map(({ runner, legNumbers, completedLegs, totalDistance, totalDuration, avgPace, elevGain, elevLoss }) => (
                      <tr
                        key={runner.id}
                        className={`border-b border-gray-100 last:border-0 ${runner.status === 'running' ? 'bg-blue-50' : ''}`}
                      >
                        <td className="px-1.5 py-1 font-bold text-gray-600">{runner.runnerNumber}</td>
                        <td className="px-1.5 py-1 font-medium text-gray-900 truncate max-w-[80px]">{runner.name}</td>
                        <td className="px-1.5 py-1 text-center">
                          {legNumbers.length > 0 ? (
                            <span className={`font-semibold ${completedLegs === legNumbers.length && legNumbers.length > 0 ? 'text-green-700' : 'text-gray-700'}`}>
                              {completedLegs}/{legNumbers.length}
                            </span>
                          ) : '—'}
                        </td>
                        <td className="px-1.5 py-1 text-center font-mono text-gray-700">
                          {totalDistance > 0 ? totalDistance.toFixed(1) : '—'}
                        </td>
                        <td className="px-1.5 py-1 text-center font-mono text-gray-700">
                          {totalDuration > 0 ? formatDuration(totalDuration) : '—'}
                        </td>
                        <td className="px-1.5 py-1 text-center font-mono text-gray-700">
                          {avgPace != null ? formatPace(avgPace) : '—'}
                        </td>
                        <td className="px-1.5 py-1 text-center font-mono text-green-700">
                          {elevGain > 0 ? elevGain.toLocaleString() : '—'}
                        </td>
                        <td className="px-1.5 py-1 text-center font-mono text-orange-700">
                          {elevLoss > 0 ? elevLoss.toLocaleString() : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}

          {/* Estimated finish time */}
          {estimatedFinishTime && (
            <div className="bg-white rounded-lg border-2 border-purple-300 px-2 py-1 flex items-center gap-2">
              <span className="text-xs font-bold text-gray-600">Est. Race Finish</span>
              <span className="text-sm font-mono font-bold text-purple-700">{estimatedFinishTime}</span>
            </div>
          )}
        </div>
      </div>
    </AppShell>
  )
}
